import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

const getAllGeographiesV2 = ({
  brand,
  category,
  product,
  startDate,
  endDate,
  geographies = [],
}: {
  brand?: string;
  category?: string;
  product?: string;
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
}): Promise<string[]> => {
  const payload: Record<string, any> = {
    brand,
    category,
    product,
    startDate,
    endDate,
    geographies,
  };

  return axiosApi.get("/api/v2/geographies/all", {
    params: payload,
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

const _getAllGeographiesNest = ({
  brand,
  category,
  product,
  startDate,
  endDate,
  geographies = [],
}: {
  brand?: string;
  category?: string;
  product?: string;
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
}): Promise<string[]> => {
  const params = new URLSearchParams();

  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);

  if (geographies && geographies.length > 0) {
    geographies.forEach((geography) => {
      params.append("geographies", geography);
    });
  }

  return axiosApi
    .get(`/api/geography/all?${params.toString()}`, {
      baseURL: NEST_BACKEND_URL,
    })
    .then((response: any) => {
      return response.data.geographies;
    });
};

export const useAllGeographies = ({
  brand,
  category,
  product,
  startDate,
  endDate,
  geographies = [],
  config,
}: {
  brand?: string;
  category?: string;
  product?: string;
  startDate?: string;
  endDate?: string;
  geographies?: string[];
  config?: QueryConfig<typeof getAllGeographiesV2 | typeof _getAllGeographiesNest>;
}) => {
  const endpointType = useModuleEndpoint("geographies");

  const queryKey = useMemo(
    () => [
      "all-geographies",
      brand,
      category,
      product,
      startDate,
      endDate,
      geographies,
      endpointType,
    ],
    [brand, category, product, startDate, endDate, geographies, endpointType]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return _getAllGeographiesNest({
        brand,
        category,
        product,
        startDate,
        endDate,
        geographies,
      });
    } else {
      return getAllGeographiesV2({
        brand,
        category,
        product,
        startDate,
        endDate,
        geographies,
      });
    }
  }, [brand, category, product, startDate, endDate, geographies, endpointType]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
  });
};
